/**
 * PageViewTracker — reports each page the user opens (Admin Settings → Usage).
 *
 * Mounted once by AppShell (both the desktop shell and the embedded one in the
 * Compass App's WebView). On every client-side pathname change it posts
 * `{ path }` to /api/page-views. Query-only changes (filters, tabs, the
 * Briefings person/week) are the same page and are not reported. Whether the
 * view counts as web or app is decided server-side from the session, so this
 * component does not look at embedded mode.
 *
 * Fire-and-forget: `keepalive` lets the request finish if the user navigates
 * away, and every failure is swallowed — analytics never affect the page.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const PAGE_VIEWS_ENDPOINT = '/api/page-views';

export function reportPageView(path: string, fetchImpl: typeof fetch = fetch): void {
  try {
    void fetchImpl(PAGE_VIEWS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({ path }),
    }).catch(() => undefined);
  } catch {
    // fetch itself can throw synchronously (e.g. keepalive body limits); ignore.
  }
}

export function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    reportPageView(pathname);
  }, [pathname]);
  return null;
}
